import { Grid, makeStyles, Switch, Typography } from '@material-ui/core';
import {
  Close as CloseIcon,
  DeleteSweep as DeleteSweepIcon,
  DragIndicator as DragIndicatorIcon,
  LayersClearTwoTone as LayersClearTwoToneIcon,
  Remove as RemoveIcon,
  SettingsBackupRestore as SettingsBackupRestoreIcon,
  Star as StarIcon,
  ViewDay as ViewDayIcon,
} from '@material-ui/icons';
import clsx from 'clsx';
import Button from 'components/Button';
import i18n from 'i18n';
import { messagesRequest } from 'modules/messages/actions';
import React from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { connect } from 'react-redux';
import { modalClose } from 'modules/modal/actions';
import DashboardCard from '../DashboardCard';
import { CardConfig } from '../interfaces';
import styles from './styles';

const useStyles = makeStyles(styles);

interface DashboardConfigurationProps {
  cardsConfig: CardConfig[];
  defaultSelectedCards: string[];
  disableDashboardText: string;
  isLoading: boolean;
  selectedCards: string[];
  setSelectedCards: React.Dispatch<React.SetStateAction<string[]>>;
  messagesRequest: (props) => void;
  modalClose: () => void;
}

const DashboardConfiguration = ({
  cardsConfig,
  defaultSelectedCards,
  disableDashboardText,
  isLoading,
  selectedCards,
  setSelectedCards,
  messagesRequest,
  modalClose,
}: DashboardConfigurationProps) => {
  const classes = useStyles();

  const [modalSelectedCards, setModalSelectedCards] = React.useState(
    selectedCards
  );
  const [collapseCards, setCollapseCards] = React.useState(false);
  const [noDashboard, setNoDashboard] = React.useState(
    modalSelectedCards.length === 0
  );

  React.useEffect(() => {
    setNoDashboard(modalSelectedCards.length === 0);
  }, [modalSelectedCards]);

  const handleCardClick = key => {
    setModalSelectedCards(prevSelected => {
      if (prevSelected.includes(key)) {
        return prevSelected.filter(item => item !== key);
      }
      if (prevSelected.length < 3) {
        return [...prevSelected, key];
      }
      return [...prevSelected.slice(0, -1), key];
    });
  };
  const onDragEnd = result => {
    const { destination, source } = result;
    if (!destination) return;

    const reorderedList = Array.from(modalSelectedCards);
    const [removed] = reorderedList.splice(source.index, 1);
    reorderedList.splice(destination.index, 0, removed);

    setModalSelectedCards(reorderedList);
  };

  React.useEffect(() => {
    cardsConfig.forEach(config => {
      if (!selectedCards.includes(config.key)) {
        config.getReport();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCards]);

  const handleChange = () => {
    setNoDashboard(prevNoDashboard => {
      if (!prevNoDashboard) {
        setModalSelectedCards([]);
      } else {
        setModalSelectedCards(defaultSelectedCards);
      }
      return !prevNoDashboard;
    });
  };

  return (
    <Grid
      container
      alignItems="center"
      spacing={2}
      className={classes.cardGrid}
      justify="flex-start"
    >
      <Grid container justify="space-between">
        <Grid item>
          <Typography className={classes.cardsTitleModal}>
            {`${i18n.t('SELECT_CARDS_TITLE_CARDS_KEY')}:`}
          </Typography>
        </Grid>
        <Grid item className={classes.switchGrid}>
          <Switch
            id="toggle-activate-inactivate"
            size="small"
            onChange={handleChange}
            checked={noDashboard}
          />
          <Typography className={classes.switchText}>
            {disableDashboardText}
          </Typography>
        </Grid>
      </Grid>
      <Grid container alignItems="center">
        <Grid item xs={3}>
          <Button
            className={classes.dashboardButtonsHeader}
            onClick={() => setModalSelectedCards(defaultSelectedCards)}
            startIcon={<SettingsBackupRestoreIcon />}
          >
            {i18n.t('RESTORE_DEFAULT_GRAPHS_KEY')}
          </Button>
        </Grid>
        <Grid item xs={6} className={classes.previewModal}>
          {modalSelectedCards.length > 0 && (
            <DragDropContext onDragEnd={result => onDragEnd(result)}>
              <Droppable droppableId="droppable" direction="horizontal">
                {provided => (
                  <Grid {...provided.droppableProps} ref={provided.innerRef}>
                    <Grid item className={classes.cardPreview}>
                      {modalSelectedCards.map((selectedKey, index) => {
                        const config = cardsConfig.find(
                          item => item.key === selectedKey
                        );
                        return (
                          <Draggable
                            key={selectedKey}
                            draggableId={selectedKey}
                            index={index}
                          >
                            {provided => (
                              <Grid
                                item
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={classes.cardPreviewItem}
                              >
                                <CloseIcon
                                  className={classes.closeIcon}
                                  onClick={() => handleCardClick(selectedKey)}
                                />
                                <Typography className={classes.cardsTitleModal}>
                                  {i18n.t(config?.title || '')}
                                </Typography>
                                <DragIndicatorIcon
                                  className={classes.dragIcon}
                                />
                              </Grid>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </Grid>
                  </Grid>
                )}
              </Droppable>
            </DragDropContext>
          )}
          {(noDashboard || modalSelectedCards.length === 0) && (
            <Grid>
              <Grid item className={classes.cardPreviewNoDash}>
                <LayersClearTwoToneIcon className={classes.noDashIcon} />
                <Typography>{i18n.t('NO_DASHBOARD_KEY')}</Typography>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={3} className={classes.optionsModal}>
          {modalSelectedCards.length > 0 && (
            <Button
              className={classes.cleanButton}
              onClick={() => {
                setModalSelectedCards([]);
              }}
              startIcon={<DeleteSweepIcon className={classes.cleanIcon} />}
            >
              {i18n.t('CLEAR_SELECTION_KEY')}
            </Button>
          )}
          {collapseCards ? (
            <Button
              className={classes.dashboardButtonsHeader}
              onClick={() => setCollapseCards(!collapseCards)}
              startIcon={<ViewDayIcon />}
            >
              {i18n.t('EXPAND_CARDS_KEY')}
            </Button>
          ) : (
            <Button
              className={classes.dashboardButtonsHeader}
              onClick={() => setCollapseCards(!collapseCards)}
              startIcon={<RemoveIcon />}
            >
              {i18n.t('MINIMIZE_CARDS_KEY')}
            </Button>
          )}
        </Grid>
      </Grid>
      {cardsConfig.map(config => (
        <Grid
          item
          key={config.key}
          className={clsx(classes.cardSelector, {
            [classes.cardSelectorActive]: modalSelectedCards.includes(
              config.key
            ),
          })}
          onClick={() => handleCardClick(config.key)}
        >
          <Grid
            item
            className={clsx(classes.modalCardHeader, {
              [classes.modalCardHeaderCollapse]: collapseCards,
            })}
          >
            <Typography className={classes.cardsTitleModal}>
              {i18n.t(config.title)}
            </Typography>
            {modalSelectedCards.includes(config.key) && (
              <StarIcon className={classes.checkIconModal} />
            )}
          </Grid>
          {!collapseCards && (
            <DashboardCard
              cardConfig={config}
              isLoading={isLoading}
              modalTitle={i18n.t(config.modalTitle)}
              filterTooltip={i18n.t(config.tooltip)}
              isConfig
            />
          )}
        </Grid>
      ))}

      <Grid container justify="flex-end">
        <Button
          id="save-config"
          submitButton
          onClick={() => {
            setSelectedCards(modalSelectedCards);
            messagesRequest({
              type: 'success',
              message: i18n.t('SUCCESSFULLY_CONFIG_DASHBOARD_KEY'),
            });
            modalClose();
          }}
        >
          {i18n.t('SAVE_KEY')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = {
  messagesRequest,
  modalClose,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DashboardConfiguration);
